import React, { type JSX } from "react";
import { ArrowDownTrayIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import type { ResearchDoc } from "./types/redux";

interface InlineResearchDocsTableProps {
  researchDocs: ResearchDoc[];
  onDocClick: (doc: ResearchDoc) => void;
}

interface ColumnDef {
  key: string;
  header: string;
  className?: string;
  render: (doc: ResearchDoc) => React.ReactNode;
}

const PLACEHOLDER = "--";

// ─── Field accessors ──────────────────────────────────────────
// Keep field access funnelled through these helpers so a backend
// rename is a one-file change (see context.md working rules).

export function getTitle(doc: ResearchDoc): string {
  return (
    doc.researchTitle ||
    doc.title ||
    doc.documentName ||
    doc.originalFilename ||
    PLACEHOLDER
  );
}

export function getPublicationDate(doc: ResearchDoc): string {
  const raw = doc.researchPublicationDate;
  if (!raw) return PLACEHOLDER;
  // Backend Snowflake column is a TIMESTAMP_TZ — fast_mcp stringifies it via
  // `str(datetime_obj)`, producing values like "2025-12-01 00:00:00+00:00".
  // When the column is a plain DATE we get "2025-03-14". Strip the time/TZ
  // suffix so both render as just the date (design hasn't asked for a
  // locale-aware format yet).
  const match = raw.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : raw;
}

export function getType(doc: ResearchDoc): string {
  // Design hasn't confirmed which of research_type / research_content_type
  // maps to the mockup "Type" column. Prefer research_content_type since
  // mockup values ("Company profile", "Market overview") read as content
  // types, with research_type as fallback.
  return doc.researchContentType || doc.researchType || PLACEHOLDER;
}

export function getSector(doc: ResearchDoc): string {
  return doc.researchSector || PLACEHOLDER;
}

export function getTeam(doc: ResearchDoc): string {
  // No backing field on DocumentRecord yet — mockup column shows `--`
  // until backend adds research_team or design drops the column.
  return doc.researchTeam || PLACEHOLDER;
}

export function getSupplier(doc: ResearchDoc): string {
  return doc.researchSupplier || PLACEHOLDER;
}

// ─── Export / copy helpers ────────────────────────────────────

const EXPORT_HEADERS = ["Title", "Pub. date", "Type", "Sector", "Team", "Supplier"] as const;

function rowFor(doc: ResearchDoc): string[] {
  return [
    getTitle(doc),
    getPublicationDate(doc),
    getType(doc),
    getSector(doc),
    getTeam(doc),
    getSupplier(doc),
  ];
}

// Research titles/suppliers/etc. originate in external datasets, so a cell
// starting with =, +, -, @, \t, or \r can be interpreted as a formula when
// the exported file is opened in Excel/Sheets. Prefix such cells with a
// single-quote neutraliser before serialisation — OWASP CSV Injection guidance.
// Our own PLACEHOLDER sentinel ("--") is safe by construction — it's never
// user content — so we skip it to keep the exported "missing-value" look tidy.
const CSV_INJECTION_PREFIX_RE = /^[=+\-@\t\r]/;
function neutraliseFormulaInjection(cell: string): string {
  if (cell === PLACEHOLDER) return cell;
  return CSV_INJECTION_PREFIX_RE.test(cell) ? `'${cell}` : cell;
}

/** TSV string for clipboard. Tabs + newlines; no escaping needed for paste. */
export function researchDocsToTsv(docs: ResearchDoc[]): string {
  const rows = docs.map((doc) => rowFor(doc).map(neutraliseFormulaInjection));
  return [EXPORT_HEADERS.join("\t"), ...rows.map((r) => r.join("\t"))].join("\n");
}

/** CSV string for .csv download. Quote every cell, double inner quotes. */
export function researchDocsToCsv(docs: ResearchDoc[]): string {
  const escape = (v: string) => `"${neutraliseFormulaInjection(v).replace(/"/g, '""')}"`;
  const rows = docs.map(rowFor);
  return [
    EXPORT_HEADERS.map((h) => `"${h.replace(/"/g, '""')}"`).join(","),
    ...rows.map((r) => r.map(escape).join(",")),
  ].join("\n");
}

/** Trigger a browser download of the docs as a .csv file. */
export function downloadResearchDocsCsv(docs: ResearchDoc[]): void {
  const csv = researchDocsToCsv(docs);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "research-docs.csv";
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Copy the docs to the clipboard as TSV. The async Clipboard API is
 * unavailable on insecure origins and when the document isn't focused, so an
 * offscreen-textarea + execCommand path backs it up.
 */
export async function copyResearchDocsTsv(docs: ResearchDoc[]): Promise<void> {
  const tsv = researchDocsToTsv(docs);
  try {
    await navigator.clipboard.writeText(tsv);
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = tsv;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
}

// ─── Columns ──────────────────────────────────────────────────

/** Every column but Title renders its accessor's value, greying out the placeholder. */
function textColumn(
  key: string,
  header: string,
  className: string,
  accessor: (doc: ResearchDoc) => string
): ColumnDef {
  return {
    key,
    header,
    className,
    render: (doc) => {
      const value = accessor(doc);
      if (value === PLACEHOLDER) return <span className="text-gray-400">{PLACEHOLDER}</span>;
      return value;
    },
  };
}

const RESEARCH_COLUMNS: ColumnDef[] = [
  {
    key: "title",
    header: "Title",
    className: "min-w-[180px]",
    render: (doc) => (
      <span className="text-cvc-blue bg-cvc-linkBg rounded-[3px] px-1 cursor-pointer hover:underline">
        {getTitle(doc)}
      </span>
    ),
  },
  textColumn("publicationDate", "Pub. date", "min-w-[100px]", getPublicationDate),
  textColumn("type", "Type", "min-w-[120px]", getType),
  textColumn("sector", "Sector", "min-w-[120px]", getSector),
  textColumn("team", "Team", "min-w-[110px]", getTeam),
  textColumn("supplier", "Supplier", "min-w-[140px]", getSupplier),
];

// ─── Component ────────────────────────────────────────────────

function DocRow({
  doc,
  columns,
  onDocClick,
}: {
  doc: ResearchDoc;
  columns: ColumnDef[];
  onDocClick: (doc: ResearchDoc) => void;
}): JSX.Element {
  return (
    <tr
      className="hover:bg-cvc-lb2 transition-colors duration-150 cursor-pointer"
      onClick={() => onDocClick(doc)}
    >
      {columns.map((col) => (
        <td
          key={col.key}
          className={`py-2 px-3 text-sm text-cvc-text truncate ${col.className || ""}`}
        >
          {col.render(doc)}
        </td>
      ))}
    </tr>
  );
}

function TableActions({ researchDocs }: { researchDocs: ResearchDoc[] }): JSX.Element {
  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-cvc-tableHeader border-t border-cvc-tableBorder">
      <button
        onClick={(e) => {
          e.stopPropagation();
          downloadResearchDocsCsv(researchDocs);
        }}
        className="inline-flex items-center gap-1.5 text-xs text-cvc-headerText hover:text-cvc-blue transition-colors"
      >
        <ArrowDownTrayIcon className="w-3.5 h-3.5" />
        Export to Excel
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          void copyResearchDocsTsv(researchDocs);
        }}
        className="inline-flex items-center gap-1.5 text-xs text-cvc-headerText hover:text-cvc-blue transition-colors"
      >
        <ClipboardDocumentIcon className="w-3.5 h-3.5" />
        Copy table
      </button>
    </div>
  );
}

export default function InlineResearchDocsTable_Component({
  researchDocs,
  onDocClick,
}: InlineResearchDocsTableProps): JSX.Element | null {
  if (researchDocs.length === 0) return null;

  return (
    <div className="mt-3 mb-1 border border-cvc-tableBorder rounded-[5px] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-cvc-tableBorder">
          <thead className="bg-cvc-tableHeader">
            <tr>
              {RESEARCH_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`py-2 px-3 text-left text-xs font-cvcBody font-normal text-cvc-headerText uppercase tracking-wider ${col.className || ""}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-cvc-tableBorder font-cvcBody">
            {researchDocs.map((doc, idx) => (
              <DocRow
                key={doc.docId || idx}
                doc={doc}
                columns={RESEARCH_COLUMNS}
                onDocClick={onDocClick}
              />
            ))}
          </tbody>
        </table>
      </div>
      <TableActions researchDocs={researchDocs} />
    </div>
  );
}
